import { useState, useEffect, useRef, useCallback } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { graphTokenRequest } from '@/authConfig';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface DriveItem {
  id: string;
  name: string;
  size: number;
  lastModifiedDateTime: string;
  folder?: { childCount: number };
  file?: { mimeType: string };
  '@microsoft.graph.downloadUrl'?: string;
  webUrl: string;
}

export interface BreadcrumbEntry {
  id: string | null;
  name: string;
}

const SHAREPOINT_HOST = 'socialsparkapp974.sharepoint.com';
const SITE_PATH = '/sites/SocialSparkWorkspace';
const GRAPH_BASE = 'https://graph.microsoft.com/v1.0';

// ─────────────────────────────────────────────────────────────────────────────
// Hook
// ─────────────────────────────────────────────────────────────────────────────

export function useSharePoint() {
  const { instance, accounts } = useMsal();

  const [items, setItems] = useState<DriveItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsConsent, setNeedsConsent] = useState(false);
  const [breadcrumb, setBreadcrumb] = useState<BreadcrumbEntry[]>([{ id: null, name: 'Home' }]);

  // Cached drive info — fetched once on mount
  const driveIdRef = useRef<string | null>(null);
  const driveReadyRef = useRef(false);

  const getToken = useCallback(async (): Promise<string> => {
    const account = accounts[0];
    if (!account) throw new Error('No authenticated account');
    try {
      const result = await instance.acquireTokenSilent({ ...graphTokenRequest, account });
      return result.accessToken;
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        setNeedsConsent(true);
        throw new ConsentNeededError();
      }
      throw err;
    }
  }, [instance, accounts]);

  // Called directly from a button click so the browser allows the popup
  const grantAccess = useCallback(async () => {
    const account = accounts[0];
    if (!account) return;
    try {
      setError(null);
      await instance.acquireTokenPopup({ ...graphTokenRequest, account });
      setNeedsConsent(false);
      driveIdRef.current = null;
      driveReadyRef.current = false;
    } catch {
      setError('Consent was cancelled or failed. Please try again.');
    }
  }, [instance, accounts]);

  const graphFetch = useCallback(async <T = unknown>(
    path: string,
    options?: RequestInit & { expectNoContent?: boolean },
  ): Promise<T> => {
    const token = await getToken();
    const { expectNoContent, ...fetchOptions } = options ?? {};
    const res = await fetch(`${GRAPH_BASE}${path}`, {
      ...fetchOptions,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(fetchOptions.headers ?? {}),
      },
    });
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      const code = (body as { error?: { code?: string; message?: string } }).error?.code ?? '';
      const msg = (body as { error?: { code?: string; message?: string } }).error?.message ?? res.statusText;
      if (res.status === 401 || res.status === 403 || code === 'AccessDenied') {
        throw new PermissionError(`Permission denied: ${msg}`);
      }
      throw new Error(msg || `Graph API error ${res.status}`);
    }
    if (expectNoContent || res.status === 204) return undefined as unknown as T;
    return res.json() as Promise<T>;
  }, [getToken]);

  // Resolve site → drive on mount
  useEffect(() => {
    if (driveReadyRef.current) return;
    let cancelled = false;

    async function init() {
      try {
        const site = await graphFetch<{ id: string }>(`/sites/${SHAREPOINT_HOST}:${SITE_PATH}`);
        const drive = await graphFetch<{ id: string }>(`/sites/${site.id}/drive`);
        if (!cancelled) {
          driveIdRef.current = drive.id;
          driveReadyRef.current = true;
        }
      } catch (err) {
        if (!cancelled) setError(formatError(err));
      }
    }

    init();
    return () => { cancelled = true; };
  }, [graphFetch]);

  const currentFolderId = breadcrumb[breadcrumb.length - 1].id;

  const fetchItems = useCallback(async () => {
    if (!driveReadyRef.current || !driveIdRef.current) {
      let attempts = 0;
      while (!driveReadyRef.current && attempts < 20) {
        await new Promise(r => setTimeout(r, 300));
        attempts++;
      }
      if (!driveReadyRef.current || !driveIdRef.current) {
        setError('Could not connect to SharePoint. Check permissions and try again.');
        return;
      }
    }

    setLoading(true);
    setError(null);
    try {
      const driveId = driveIdRef.current!;
      const path = currentFolderId
        ? `/drives/${driveId}/items/${currentFolderId}/children`
        : `/drives/${driveId}/root/children`;
      const data = await graphFetch<{ value: DriveItem[] }>(
        `${path}?$select=id,name,size,lastModifiedDateTime,folder,file,webUrl`,
      );
      setItems(data.value ?? []);
    } catch (err) {
      setError(formatError(err));
    } finally {
      setLoading(false);
    }
  }, [graphFetch, currentFolderId]);

  useEffect(() => {
    fetchItems();
  }, [fetchItems]);

  const navigate = useCallback((item: DriveItem) => {
    setBreadcrumb(prev => [...prev, { id: item.id, name: item.name }]);
  }, []);

  const goTo = useCallback((index: number) => {
    setBreadcrumb(prev => prev.slice(0, index + 1));
  }, []);

  // ── File operations ────────────────────────────────────────────────────────

  const upload = useCallback(async (file: File): Promise<boolean> => {
    if (!driveIdRef.current) return false;
    try {
      const driveId = driveIdRef.current;
      const path = currentFolderId
        ? `/drives/${driveId}/items/${currentFolderId}:/${encodeURIComponent(file.name)}:/content`
        : `/drives/${driveId}/root:/${encodeURIComponent(file.name)}:/content`;
      await graphFetch(path, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      await fetchItems();
      return true;
    } catch (err) {
      setError(formatError(err));
      return false;
    }
  }, [graphFetch, currentFolderId, fetchItems]);

  const createFolder = useCallback(async (name: string): Promise<boolean> => {
    if (!driveIdRef.current) return false;
    try {
      const driveId = driveIdRef.current;
      const path = currentFolderId
        ? `/drives/${driveId}/items/${currentFolderId}/children`
        : `/drives/${driveId}/root/children`;
      await graphFetch(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, folder: {}, '@microsoft.graph.conflictBehavior': 'rename' }),
      });
      await fetchItems();
      return true;
    } catch (err) {
      setError(formatError(err));
      return false;
    }
  }, [graphFetch, currentFolderId, fetchItems]);

  const renameItem = useCallback(async (item: DriveItem, newName: string): Promise<boolean> => {
    if (!driveIdRef.current) return false;
    try {
      const driveId = driveIdRef.current;
      await graphFetch(`/drives/${driveId}/items/${item.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName }),
      });
      await fetchItems();
      return true;
    } catch (err) {
      setError(formatError(err));
      return false;
    }
  }, [graphFetch, fetchItems]);

  const deleteItem = useCallback(async (item: DriveItem): Promise<boolean> => {
    if (!driveIdRef.current) return false;
    try {
      const driveId = driveIdRef.current;
      await graphFetch(`/drives/${driveId}/items/${item.id}`, {
        method: 'DELETE',
        expectNoContent: true,
      });
      await fetchItems();
      return true;
    } catch (err) {
      setError(formatError(err));
      return false;
    }
  }, [graphFetch, fetchItems]);

  return {
    items,
    loading,
    error,
    needsConsent,
    grantAccess,
    breadcrumb,
    navigate,
    goTo,
    upload,
    createFolder,
    renameItem,
    deleteItem,
    refetch: fetchItems,
  };
}

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

class PermissionError extends Error {
  constructor(msg: string) { super(msg); this.name = 'PermissionError'; }
}

class ConsentNeededError extends Error {
  constructor() { super('consent_needed'); this.name = 'ConsentNeededError'; }
}

function formatError(err: unknown): string {
  if (err instanceof ConsentNeededError) return '';
  if (err instanceof PermissionError) {
    return 'PERMISSION: You need Microsoft Graph access (Files.ReadWrite.All). Ask your admin to grant consent in the Azure portal.';
  }
  if (err instanceof Error) return err.message;
  return 'An unknown error occurred';
}
